import { isEmpty } from 'lodash-es';
import {
  reorder,
  deleteItem,
  update,
  create,
  copy,
  get,
} from '@/api/widgets';
import filterLinks from '@/utils/filterLinks';

const initialState = () => ({
  list: [],
  search: '',
  idToDelete: null,
  idToEdit: null,
  creationWidgetId: null,
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    set(state, widgets) {
      state.list = widgets;
    },
    setSearch(state, search) {
      state.search = search;
    },
    setIdToDelete(state, id) {
      state.idToDelete = id;
    },
    setIdToEdit(state, id) {
      state.idToEdit = typeof id === 'string' ? id : null;
    },
    removeOne(state, id) {
      state.list = state.list.filter(item => item.data.id !== id);
    },
    addOne(state, payload) {
      state.list.push(payload);
    },
    reorder(state) {
      state.list = state.list.sort((widget1, widget2) => widget1.data.order - widget2.data.order);
    },
    setCreationWidgetId(state, creationWidgetId) {
      state.creationWidgetId = creationWidgetId;
    },
  },
  actions: {
    async reorder({ commit }, list) {
      commit('set', list);
      const widgets = list.map((item, i) => Object.assign({}, item, {
        order: i,
        id: item.data.id,
      }));
      await reorder(widgets);
    },
    async closeModal({ commit }) {
      commit('setIdToEdit', null);
      commit('setCreationWidgetId', null);
      commit('dashboard/editorPopup/closeAddPopup', true, { root: true });
    },
    async updateOne({ commit, getters, dispatch }) {
      const payload = { linkType: getters.widgetToEdit.linkType, ...getters.widgetToEdit.data };
      try {
        await update(payload);
        commit('setIdToEdit', null);
        commit('setCreationWidgetId', null);
        commit('dashboard/editorPopup/closeAddPopup', true, { root: true });

        dispatch('user/updateSetupProgress', { link: true }, { root: true });
      } catch (e) {
        alert(e);
      }
    },
    async deleteOne({ commit, getters, dispatch }) {
      const payload = { linkType: getters.widgetToDelete.linkType, id: getters.idToDelete };
      try {
        const id = getters.idToDelete;
        commit('setIdToDelete', null);
        await deleteItem(payload);
        commit('removeOne', id);
        commit('previewScreenWidgets/setWidgets', getters.list, { root: true });
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      }
    },
    async create({ commit, dispatch, getters }, payload) {
      try {
        const { data } = await create(payload);
        commit('addOne', data);
        commit('previewScreenWidgets/setWidgets', getters.list, { root: true });
        commit('setIdToEdit', data.data.id);
        commit('setCreationWidgetId', data.data.id);

        return data;
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      }

      return undefined;
    },
    async abortWidgetCreation({ state, commit, dispatch }) {
      const { creationWidgetId } = state;
      if (creationWidgetId) {
        commit('setIdToDelete', creationWidgetId);
        await dispatch('deleteOne');
        commit('setCreationWidgetId', null);
      }
    },
    async copy({ commit }, payload) {
      try {
        const { data } = await copy(payload);
        commit('addOne', data);
        commit('reorder');
      } catch (e) {
        alert(e);
      }
    },
    async initWidgets({ dispatch, getters }) {
      if (isEmpty(getters.list)) {
        await dispatch('loadWidgets');
      }
    },
    async loadWidgets({ commit }) {
      const { data: widgets } = await get();
      commit('set', widgets);
      commit('previewScreenWidgets/setWidgets', widgets, { root: true });
    },
    async clearLinks({ commit }) {
      commit('set', []);
      commit('previewScreenWidgets/setWidgets', [], { root: true });
    },
  },
  getters: {
    list: state => state.list || [],
    filteredList: (state, getters) => filterLinks(state.search, getters.list),
    idToDelete: state => state.idToDelete,
    widgetToEdit: state => state.list.find(link => link.data.id === state.idToEdit),
    widgetToDelete: state => state.list.find(link => link.data.id === state.idToDelete),
    search: state => state.search,
  },
};
